/**
 * Partner portal home — route /partner. Lists the partner's own campaigns,
 * each linking to its tracking view. Redirects to /partner/login if not signed in.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PartnerShell } from '../components/PartnerShell';
import { ApiError, type CampaignRead } from '../lib/api';
import { listMyCampaigns } from '../lib/partnerApi';
import { isPartnerAuthed } from '../lib/partnerAuth';

export function PartnerCampaignsPage() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<CampaignRead[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isPartnerAuthed()) {
      navigate('/partner/login', { replace: true });
      return;
    }
    let live = true;
    listMyCampaigns()
      .then((c) => live && setCampaigns(c))
      .catch((err) => {
        if (!live || (err instanceof ApiError && err.status === 401)) return;
        setError(err instanceof ApiError ? err.message : 'Could not load your campaigns.');
      });
    return () => {
      live = false;
    };
  }, [navigate]);

  return (
    <PartnerShell title="Your campaigns">
      <div className="mb-5">
        <Link
          to="/build"
          className="inline-block rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
        >
          + Create campaign
        </Link>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      {!error && !campaigns && <p className="text-sm text-ink-500">Loading…</p>}
      {campaigns && campaigns.length === 0 && (
        <p className="text-sm text-ink-500">You have no campaigns yet.</p>
      )}
      {campaigns && campaigns.length > 0 && (
        <ul className="grid gap-4 sm:grid-cols-2">
          {campaigns.map((c) => (
            <li key={c.id}>
              <Link
                to={`/partner/campaigns/${c.id}`}
                className="block rounded-2xl border border-line bg-white p-5 shadow-card transition hover:border-primary/40 hover:shadow-card-hover"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-semibold text-ink">{c.name}</span>
                  <span className="shrink-0 rounded-full bg-surface-muted px-2 py-0.5 text-xs font-medium text-ink-700">
                    {c.status}
                  </span>
                </div>
                <p className="mt-1 truncate text-xs text-ink-500">
                  {c.prize_title ?? 'No prize set'}
                </p>
                <span className="mt-3 inline-block text-sm font-medium text-primary">
                  Track this campaign →
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </PartnerShell>
  );
}

export default PartnerCampaignsPage;
